import * as React from "react";
import { useState } from "react";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuList,
  NavigationMenuLink,
} from "@/components/ui/navigation-menu";
import navigation from "@/config/navigation.json";
import Link from "next/link";
import clsx from "clsx";
import LeftSubmenu from "../elements/leftSubmenu/LeftSubmenu";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useDirection } from "@/context/app.context";

type MenuItem = {
  id: number;
  name: string;
  path: string;
  hasChildren?: boolean;
  children?: MenuItem[];
};

const menuData = navigation.header as MenuItem[];

type Props = {
  textColor?: string;
  className?: string;
};

const Menu = ({ textColor, className }: Props) => {
  const [hoveredChildMenuId, setHoveredChildMenuId] = useState<number | null>(
    null,
  );
  const { direction } = useDirection();
  let timeoutId: ReturnType<typeof setTimeout>;

  const handleMouseEnter = (id: number) => {
    clearTimeout(timeoutId);
    setHoveredChildMenuId(id);
  };

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setHoveredChildMenuId(null);
    }, 200);
  };

  return (
    <NavigationMenu dir={direction as "rtl" | "ltr"}>
      <NavigationMenuList>
        {menuData.map((menu) => (
          <NavigationMenuItem key={menu.id} className={cn(className)}>
            {menu.hasChildren ? (
              <>
                <NavigationMenuTrigger
                  className={cn(
                    "bg-inherit submenu-trigger text-[20px] leading-[1] px-[15px] py-[37px] h-full font-normal",
                    textColor,
                  )}
                >
                  {menu.name}
                </NavigationMenuTrigger>
                {menu.children && menu.children.length && (
                  <NavigationMenuContent className="border-0 bg-white transition-none">
                    <NavigationMenuList
                      className={clsx(
                        "py-[18px] px-0  w-[240px] gap-4 grid grid-cols-1",
                        menu.id === 1 && "w-[500px] grid grid-cols-2",
                      )}
                    >
                      {menu.children.map((childMenu, j) => (
                        <NavigationMenuItem
                          key={childMenu.id}
                          className="px-[25px] relative  ease-in transition-all duration-300 transform hover:scale-105"
                          onMouseEnter={() => handleMouseEnter(childMenu.id)}
                          onMouseLeave={handleMouseLeave}
                        >
                          <NavigationMenuLink asChild className="px-0 relative">
                            <>
                              <Link
                                className="px-0 flex justify-between text-black"
                                href={childMenu.path}
                              >
                                {childMenu.name}
                                {childMenu.hasChildren && (
                                  <>
                                    {direction === "rtl" ? (
                                      <ChevronLeft className="text-black" />
                                    ) : (
                                      <ChevronRight className="text-black" />
                                    )}
                                  </>
                                )}
                              </Link>
                              {childMenu.hasChildren &&
                                hoveredChildMenuId === childMenu.id && (
                                  <LeftSubmenu submenuData={childMenu} />
                                )}
                            </>
                          </NavigationMenuLink>
                        </NavigationMenuItem>
                      ))}
                    </NavigationMenuList>
                  </NavigationMenuContent>
                )}
              </>
            ) : (
              <NavigationMenuLink
                asChild
                className={cn(
                  "text-[20px] leading-[1] px-[15px] py-[37px] h-full",
                  textColor,
                )}
              >
                <Link href={menu.path}>{menu.name}</Link>
              </NavigationMenuLink>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default Menu;
